import React, { useState } from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatDate } from 'helpers/dateHelper';
import { extractRawDateYear } from 'routes/HumanResourcesModule/Simulation/helpers/dataHelpers';
import { useGetHrSimulationPayConstants } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/helpers/hooks/payConstants';
import { useGetHrSimulationEchelonsHypotheses } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/helpers/hooks/echelons';
import EchelonsHypothesesModal from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/components/EchelonsHypotheses/EchelonsHypothesesModal';
import { Hypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/helpers/types';
import Flex from 'ui/Flex/Flex';
import { currencyFormatter, percentageFormatter } from 'helpers/formatHelper';
import { useGetHrFreeHypotheses } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/hooks/freeHypothesis';
import GVTHypothesisResume from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Resume/GVTResume/components/GVTHypothesisResume';
import HypothesesResumeLoader from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Resume/GVTResume/components/HypothesesResumeLoader';
import { getEchelonResume } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Resume/helpers/dataHelpers';
import IndexRaiseHypothesisResume from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Resume/GVTResume/components/IndexRaiseHypothesisResume';
import FreeHypothesisListModal from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/FreeHypothesisListModal';
import FreeHypotheses from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Resume/GVTResume/components/FreeHypothesesResume';
import Accordion from 'components/SharedComponents/Accordion/Accordion';

const StyContainer = styled(Box, { name: 'StyContainer' })({
    display: 'flex',
    gap: '1.5rem',
    marginTop: '1rem',
    flexDirection: 'column',
});

type BasicHypothesisInfo = {
    titleTranslation: string;
    hypothesis: Hypothesis | undefined;
    valueFormatter: (value: number) => string;
};

type Props = {
    hrSimulation: HrSimulation;
};

const GVTResume = ({ hrSimulation }: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { id, startDate, endDate } = hrSimulation;
    const [showEchelonModal, setShowEchelonModal] = useState(false);
    const [showFreeHypothesesList, setShowFreeHypothesesList] = useState(false);
    const firstYear = extractRawDateYear(startDate);
    const lastYear = extractRawDateYear(endDate);
    const {
        data: hrSimulationPayConstantResponse,
        isLoading: isHrSimulationPayConstantLoading,
        isFetching: isHrSimulationPayConstantFetching,
    } = useGetHrSimulationPayConstants(id);
    const {
        data: echelonHypothesesData,
        isLoading: isEchelonHypothesesDataLoading,
    } = useGetHrSimulationEchelonsHypotheses(hrSimulation.id);

    const { data: freeHypothesesData, isLoading: isFreeHypothesesLoading } =
        useGetHrFreeHypotheses(hrSimulation.id);
    const freeHypotheses = freeHypothesesData ?? [];

    const isLoading =
        isHrSimulationPayConstantLoading ||
        isHrSimulationPayConstantFetching ||
        isEchelonHypothesesDataLoading ||
        isFreeHypothesesLoading;

    const {
        indexPointHypothesis,
        indexRaiseHypothesis,
        rateCnraclHypothesis,
        smicHypothesis,
    } = hrSimulationPayConstantResponse ?? {};
    const echelonHypotheses = echelonHypothesesData ?? [];

    const basicHypothesesInfos: Array<BasicHypothesisInfo> = [
        {
            titleTranslation: 'valuePointEvolution',
            hypothesis: indexPointHypothesis,
            valueFormatter: currencyFormatter,
        },
        {
            titleTranslation: 'valueSmicEvolution',
            hypothesis: smicHypothesis,
            valueFormatter: currencyFormatter,
        },
        {
            titleTranslation: 'rateCNRACLEvolution',
            hypothesis: rateCnraclHypothesis,
            valueFormatter: (value) => percentageFormatter(value / 100),
        },
    ];
    const hypothesisResumeContent = parse(
        t('humanResources.simulation.freeHypothesisEvolutionContent', {
            newValue: indexRaiseHypothesis?.value,
            date: formatDate(new Date(indexRaiseHypothesis?.date ?? '')),
        }),
    );

    return (
        <Accordion
            title={t('humanResources.simulation.GVTResume')}
            defaultExpanded={false}
        >
            <StyContainer>
                {isLoading ? (
                    <HypothesesResumeLoader />
                ) : (
                    <>
                        <Flex alignItems="last baseline">
                            <GVTHypothesisResume
                                title={t(
                                    'humanResources.simulation.echelonRaise',
                                )}
                                hypothesisResumeContent={parse(
                                    getEchelonResume(
                                        echelonHypotheses,
                                        firstYear,
                                        lastYear,
                                        t,
                                    ),
                                )}
                            />
                            <Button
                                onClick={() => setShowEchelonModal(true)}
                                sx={{ padding: '0 8px', minHeight: 0 }}
                            >
                                <Typography color="primary">
                                    {t('humanResources.simulation.seeDetails')}
                                </Typography>
                            </Button>
                        </Flex>
                        {showEchelonModal && (
                            <EchelonsHypothesesModal
                                hrSimulation={hrSimulation}
                                showModal={showEchelonModal}
                                setShowModal={setShowEchelonModal}
                            />
                        )}
                        {basicHypothesesInfos.map(
                            ({
                                titleTranslation,
                                hypothesis,
                                valueFormatter,
                            }) => (
                                <GVTHypothesisResume
                                    key={titleTranslation}
                                    title={t(
                                        `humanResources.simulation.${titleTranslation}`,
                                    )}
                                    hypothesisResumeContent={parse(
                                        t(
                                            'humanResources.simulation.hypothesisEvolutionContent',
                                            {
                                                oldValue: valueFormatter(
                                                    hypothesis?.defaultValue ??
                                                        0,
                                                ),
                                                newValue: valueFormatter(
                                                    hypothesis?.value ?? 0,
                                                ),
                                                date: formatDate(
                                                    new Date(
                                                        hypothesis?.date ?? '',
                                                    ),
                                                ),
                                            },
                                        ),
                                    )}
                                />
                            ),
                        )}
                        {indexRaiseHypothesis && (
                            <IndexRaiseHypothesisResume
                                key="valuePointRaiseEvolution"
                                title={t(
                                    `humanResources.simulation.valuePointRaiseEvolution`,
                                )}
                            >
                                {hypothesisResumeContent}
                            </IndexRaiseHypothesisResume>
                        )}
                        {freeHypotheses.length > 1 ? (
                            <>
                                <Flex
                                    direction="column"
                                    alignItems="flex-start"
                                >
                                    <FreeHypotheses
                                        freeHypotheses={[freeHypotheses[0]]}
                                    />
                                    <Button
                                        onClick={() =>
                                            setShowFreeHypothesesList(true)
                                        }
                                        sx={{ padding: '0 8px', minHeight: 0 }}
                                    >
                                        <Typography color="primary">
                                            {t(
                                                'humanResources.simulation.freeHypothesis.seeMore',
                                            )}
                                        </Typography>
                                    </Button>
                                </Flex>
                                {showFreeHypothesesList && (
                                    <FreeHypothesisListModal
                                        hrSimulation={hrSimulation}
                                        showModal={showFreeHypothesesList}
                                        setShowModal={setShowFreeHypothesesList}
                                        freeHypotheses={freeHypotheses}
                                    />
                                )}
                            </>
                        ) : (
                            <FreeHypotheses
                                freeHypotheses={freeHypotheses ?? []}
                            />
                        )}
                    </>
                )}
            </StyContainer>
        </Accordion>
    );
};

export default GVTResume;
